/**
 * Turning a fleet row into the words and the ordering a person reads.
 *
 * All of it is pure: a row in, labels out. The page does the fetching and the
 * markup, which keeps the wording — and, more importantly, the judgement about
 * what counts as "needs attention" — testable without a browser.
 */

import { CLOCK_SKEW_WARN_MS } from '@pbtv/shared';
import type { FleetScreen, PlaybackState, ScreenHealth, UpdateState } from '@pbtv/shared';
import { formatBytes, formatClockSkew } from './format.js';

export type Tone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger';

export function healthLabel(health: ScreenHealth): string {
  switch (health) {
    case 'online':
      return 'Online';
    case 'offline':
      return 'Offline';
    case 'closed':
      return 'Closed';
    default:
      return 'Never seen';
  }
}

export function healthTone(health: ScreenHealth): Tone {
  switch (health) {
    case 'online':
      return 'success';
    case 'offline':
      return 'danger';
    case 'closed':
      return 'neutral';
    default:
      return 'warning';
  }
}

/** The one-line explanation under the chip, so "Closed" is not read as a fault. */
export function healthExplanation(health: ScreenHealth): string {
  switch (health) {
    case 'online':
      return 'Heartbeating normally.';
    case 'offline':
      // Not "during opening hours" any more: a screen set to run 24 hours is judged
      // against the clock, so this state is now reachable while its store is shut.
      return 'No heartbeat for longer than the offline threshold, at an hour this screen was expected to be running.';
    case 'closed':
      return 'Outside this store’s opening hours, so silence is expected.';
    default:
      return 'Paired but has never checked in. Confirm the stick has power and a network.';
  }
}

export function playbackStateLabel(state: PlaybackState | null): string {
  switch (state) {
    case 'playing':
      return 'Playing';
    case 'closed':
      return 'Closed screen';
    case 'idle':
      return 'Waiting for content';
    case 'pairing':
      return 'Showing a pairing code';
    case 'updating':
      return 'Installing an update';
    default:
      return 'Not reported';
  }
}

export function updateStateLabel(state: UpdateState | null): string {
  switch (state) {
    case 'downloading':
      return 'Downloading an update';
    case 'staged':
      return 'Update staged';
    case 'awaiting-confirmation':
      return 'Update waiting on a remote press';
    case 'installing':
      return 'Installing an update';
    default:
      return 'Up to date';
  }
}

export interface ScreenFlag {
  tone: Tone;
  label: string;
  detail: string;
}

/**
 * Everything about a screen that someone should act on, in the order they
 * should act on it. An empty result means the screen is genuinely fine.
 */
export function screenFlags(row: FleetScreen): ScreenFlag[] {
  const flags: ScreenFlag[] = [];

  if (row.grantsOk === false) {
    flags.push({
      tone: 'danger',
      label: 'Permissions lost',
      detail:
        'The one-time ADB grants are gone, so this stick will not start itself after the next reboot. Re-run the grants from the runbook.',
    });
  }

  if (row.errorCount > 0) {
    flags.push({
      tone: 'warning',
      label: row.errorCount === 1 ? '1 content error' : `${row.errorCount} content errors`,
      detail: 'The device reported downloads or playback that failed.',
    });
  }

  if (row.updateState === 'awaiting-confirmation') {
    flags.push({
      tone: 'warning',
      label: 'Update waiting',
      detail: 'A staged update needs one press of Select on this TV’s remote.',
    });
  }

  if (row.clockSkewMs !== null && Math.abs(row.clockSkewMs) > CLOCK_SKEW_WARN_MS) {
    flags.push({
      tone: 'warning',
      label: `Clock ${formatClockSkew(row.clockSkewMs)}`,
      detail: 'Dayparts will change at the wrong moment until the device clock is corrected.',
    });
  }

  if (row.alwaysOn) {
    flags.push({
      tone: 'neutral',
      label: 'Runs 24 hours',
      detail:
        'This screen ignores its store’s opening hours and plays around the clock, which is what a window display facing the street usually wants. It is also watched for faults overnight rather than being assumed dark with the shop.',
    });
  }

  if (row.screen.autoStart === false) {
    flags.push({
      tone: 'neutral',
      label: 'Auto-start off',
      detail:
        'This TV will not put the menu board up by itself, and pressing HOME on the remote leaves it on the Fire TV home screen. The stick still checks in every minute, so switching it back on from here brings the screen up without anybody visiting the shop.',
    });
  }

  return flags;
}

/**
 * The flags that mean something is *wrong*.
 *
 * A neutral flag records a deliberate setting, and a screen somebody chose to
 * leave dark must not sit at the top of the attention list for ever pretending to
 * be broken, nor inflate the count in the page's opening sentence. Everything that
 * asks "does this need somebody" asks it through here; `screenFlags` stays the
 * answer to "what should this row say".
 */
function faultFlags(row: FleetScreen): ScreenFlag[] {
  return screenFlags(row).filter((flag) => flag.tone !== 'neutral');
}

export function storageSummary(row: FleetScreen): string {
  return row.storageFreeBytes === null ? 'Not reported' : `${formatBytes(row.storageFreeBytes)} free`;
}

// ---------------------------------------------------------------------------
// Ordering
// ---------------------------------------------------------------------------

export type FleetSort = 'attention' | 'store';

function attentionRank(row: FleetScreen): number {
  if (row.health === 'offline') return 0;
  if (row.health === 'never-seen') return 1;
  if (faultFlags(row).length > 0) return 2;
  if (row.health === 'closed') return 3;
  return 4;
}

function byStoreThenName(left: FleetScreen, right: FleetScreen): number {
  const store = left.storeName.localeCompare(right.storeName);
  if (store !== 0) return store;
  return left.screen.name.localeCompare(right.screen.name);
}

/**
 * "Attention" leads with what is broken and only then reads alphabetically, so
 * an owner opening the page sees the dead TV before the twenty healthy ones.
 */
export function sortFleet(rows: readonly FleetScreen[], sort: FleetSort): FleetScreen[] {
  const copy = [...rows];
  if (sort === 'store') {
    copy.sort(byStoreThenName);
    return copy;
  }
  copy.sort((left, right) => {
    const rank = attentionRank(left) - attentionRank(right);
    return rank !== 0 ? rank : byStoreThenName(left, right);
  });
  return copy;
}

export interface FleetTally {
  total: number;
  online: number;
  offline: number;
  closed: number;
  neverSeen: number;
  needsAttention: number;
}

export function tallyFleet(rows: readonly FleetScreen[]): FleetTally {
  const tally: FleetTally = {
    total: rows.length,
    online: 0,
    offline: 0,
    closed: 0,
    neverSeen: 0,
    needsAttention: 0,
  };

  for (const row of rows) {
    if (row.health === 'online') tally.online += 1;
    else if (row.health === 'offline') tally.offline += 1;
    else if (row.health === 'closed') tally.closed += 1;
    else tally.neverSeen += 1;

    if (row.health === 'offline' || row.health === 'never-seen' || faultFlags(row).length > 0) {
      tally.needsAttention += 1;
    }
  }

  return tally;
}
